import {
    Drawer,
    Box,
    Typography,
    Divider,
    Stack,
    Button,
    Autocomplete,
    TextField,
    useTheme,
    Slider,
    Switch,
    Snackbar,
    Alert,
} from "@mui/material";
import type { Parameters } from "../pages/GenerateParameters";
import { memo, useCallback, useMemo, useState } from "react";
import CustomToggleButton from "./default/CustomToggleButton";
import type { IAdditionalParameter } from "../types/parameter";
import MissingValuesDialog from "./MissingValuesDialog";

interface RecipeDrawerProps {
    open: boolean;
    onClose: () => void;
    selectedParameters: Parameters;
    additionalParameters: IAdditionalParameter;
    updateAdditionalParameters: <K extends keyof IAdditionalParameter>(key: K, value: IAdditionalParameter[K]) => void;
    parameterChange: (label: string, newSelected: string[]) => void;
    tagClick: (label: string, itemToRemove: string) => void;
    onSubmit: () => void;
}

const MemoizedMissingValuesDialog = memo(MissingValuesDialog);

export default function RecipeDrawer({
    open,
    onClose,
    selectedParameters,
    additionalParameters,
    updateAdditionalParameters,
    parameterChange,
    tagClick,
    onSubmit,
}: RecipeDrawerProps) {
    const theme = useTheme();
    const [level, setLevel] = useState<"Amateur" | "Home-Cook" | "Professional" | null>(null);

    const levels = ["Amateur", "Home-Cook", "Professional"];

    const [openMissingIngredientModal, setOpenMissingIngredientModal] = useState(false);
    const [toast, setToast] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
        open: false,
        message: "",
        severity: "success",
    });

    const handleLevelChange = useCallback(
        (item: "Amateur" | "Home-Cook" | "Professional" | null) => {
            const newLevel = level === item ? null : item;
            setLevel(newLevel);
            updateAdditionalParameters("level", newLevel);
        },
        [level, updateAdditionalParameters]
    );

    const handleSubmit = useCallback(() => {
        onSubmit();
        onClose();
    }, [onClose, onSubmit]);

    const handleCookTimeChange = useCallback(
        (e: Event, value: number | number[]) => {
            const cookTimeValue = Array.isArray(value) ? value[0] : value;
            updateAdditionalParameters("cookTime", cookTimeValue);
        },
        [updateAdditionalParameters]
    );

    const cookTimeMarks = useMemo(
        () =>
            Array.from({ length: 10 }, (_, i) => ({
                value: i * 0.5,
                label: i === 9 ? "4+" : (i * 0.5).toString(),
            })),
        []
    );

    const renderAutocompletes = useMemo(() => {
        const INGREDIENT_MISSING_OPTION = "Missing Ingredient?";

        return selectedParameters.map((parameter) => (
            <Autocomplete
                key={parameter.label}
                multiple
                options={
                    parameter.label.toLowerCase() === "ingredients" ? [INGREDIENT_MISSING_OPTION, ...parameter.options] : parameter.options
                }
                value={parameter.selected}
                onChange={(_, value) => {
                    // If "Missing Ingredient?" is clicked, open modal and don't add to selection
                    if (value.includes(INGREDIENT_MISSING_OPTION)) {
                        setOpenMissingIngredientModal(true);
                        return;
                    }
                    parameterChange(parameter.label, value);
                }}
                renderOption={(props, option) =>
                    option === INGREDIENT_MISSING_OPTION ? (
                        <li
                            {...props}
                            style={{
                                color: theme.palette.primary.main,
                                fontStyle: "italic",
                                cursor: "pointer",
                                fontWeight: 500,
                                backgroundColor: "transparent",
                            }}
                            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                            {option}
                        </li>
                    ) : (
                        <li {...props}>{option}</li>
                    )
                }
                renderInput={(params) => <TextField {...params} label={capitalizeAndEdit(parameter.label)} variant="standard" fullWidth />}
            />
        ));
    }, [parameterChange, selectedParameters, theme.palette.primary.main]);

    return (
        <Drawer anchor="left" open={open} onClose={onClose}>
            <Box
                sx={{
                    width: { xs: "100vw", sm: 420 },
                    p: 4,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    bgcolor: theme.palette.background.default,
                }}
            >
                <Typography variant="h5" fontWeight={700}>
                    Recipe Preferences
                </Typography>

                <Divider />
                <Box
                    sx={{
                        px: 1,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                        bgcolor: theme.palette.background.default,
                        flex: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        "&::-webkit-scrollbar": { width: 6 },
                    }}
                >
                    <Box>
                        <Stack spacing={3}>{renderAutocompletes}</Stack>
                    </Box>
                    {/* Additional parameter settings, same as before */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography sx={{ flexGrow: 1 }}>Use ALL selected ingredients?</Typography>
                        <Switch
                            checked={additionalParameters.strict}
                            onChange={() => updateAdditionalParameters("strict", !additionalParameters.strict)}
                        />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography sx={{ flexGrow: 1 }}>Allow additional ingredients?</Typography>
                        <Switch
                            checked={additionalParameters.additional}
                            onChange={() => updateAdditionalParameters("additional", !additionalParameters.additional)}
                        />
                    </Box>
                    <Divider />
                    <Box>
                        <Typography>Recipe complexity</Typography>
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                            {levels.map((item) => (
                                <CustomToggleButton
                                    key={item}
                                    value={item}
                                    selected={level === item}
                                    onClick={() => {
                                        if (item === null || item === "Amateur" || item === "Home-Cook" || item === "Professional") {
                                            handleLevelChange(item);
                                        }
                                    }}
                                    sx={{ mx: 1, maxWidth: "22vw" }}
                                >
                                    <Typography variant="body2">{item}</Typography>
                                </CustomToggleButton>
                            ))}
                        </Box>
                    </Box>
                    <Divider />
                    <Box sx={{ mt: 1, p: 2 }}>
                        <Typography>Max Cook Time (Hours)</Typography>
                        <Slider
                            value={additionalParameters.cookTime}
                            onChange={handleCookTimeChange}
                            step={0.5}
                            min={0.5}
                            max={4.5}
                            marks={cookTimeMarks}
                        />
                    </Box>
                </Box>
                <Divider />
                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                    <Button onClick={onClose} color="inherit" variant="outlined">
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSubmit} sx={{ color: "white" }}>
                        Generate
                    </Button>
                </Stack>
            </Box>

            <MemoizedMissingValuesDialog
                openMissingModal={openMissingIngredientModal}
                setOpenMissingModal={setOpenMissingIngredientModal}
                setToast={setToast}
            />

            <Snackbar
                open={toast.open}
                autoHideDuration={3500}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setToast((t) => ({ ...t, open: false }))}
                    severity={toast.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </Drawer>
    );
}

function capitalizeAndEdit(str: string) {
    if (str === "Allergies") return str.charAt(0).toUpperCase() + str.slice(1) + " / Exclusions";

    return str.charAt(0).toUpperCase() + str.slice(1);
}
